import React, { useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useCart } from "../../context/CartContext";
import { Link } from "react-router-dom";
import useOrderTotals from "../../hooks/useOrderTotals";
import usePricingRules from "../../hooks/usePricingRules";
import { FaTrash } from "react-icons/fa";
import { formatPrice, taxLabel } from "../../utils/formatPrice";
import colors from "../../constants/colors";

const CartPage = () => {
  const { cartItems, updateCartItemQuantity, removeFromCart, clearCart } = useCart();

  // Todos os valores monetários do resumo vêm da API; esta página não faz contas.
  const orderLines = useMemo(
    () =>
      (cartItems || []).map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
      })),
    [cartItems]
  );

  const {
    orderTotals,
    loading: totalsLoading,
    error: totalsError,
    renderAmount,
  } = useOrderTotals(orderLines);
  const pricingRules = usePricingRules();

  const handleQuantityChange = (id, quantity) => {
    // Campo vazio dá NaN, que ficava gravado no localStorage e punha todos os
    // recálculos de totais em 400 — mesmo depois de recarregar a página.
    const parsed = parseInt(quantity);
    if (Number.isNaN(parsed)) return;
    updateCartItemQuantity(id, parsed);
  };

  const freeShippingMessage = () => {
    const remaining = orderTotals?.freeShippingRemaining;
    if (typeof remaining === "number" && remaining > 0) {
      return `Faltam ${formatPrice(remaining)}€ para teres entrega gratuita.`;
    }

    if (orderTotals && (remaining <= 0 || orderTotals.shippingCost === 0)) {
      return "A tua encomenda tem entrega gratuita.";
    }

    if (pricingRules && typeof pricingRules.freeShippingThreshold === "number") {
      return `Entrega gratuita para compras acima de ${formatPrice(
        pricingRules.freeShippingThreshold
      )}€.`;
    }

    return null;
  };

  if (!cartItems || cartItems.length === 0) {
    return (
      <Container className="py-5">
        <Card className="shadow-sm" style={{ borderColor: colors.secondary }}>
          <Card.Body className="text-center p-5">
            <i
              className="bi bi-cart-x"
              style={{ fontSize: "4rem", color: colors.secondary }}
            ></i>
            <h2 className="mt-4 mb-3" style={{ color: colors.dark }}>
              O teu carrinho está vazio
            </h2>
            <p className="mb-4" style={{ color: colors.textGray }}>
              Parece que ainda não adicionaste nenhum item ao teu carrinho.
            </p>
            <Button
              as={Link}
              to="/produtos"
              size="lg"
              style={{ backgroundColor: colors.secondary, borderColor: colors.secondary, color: colors.light }}
            >
              Continuar a comprar
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h1 className="mb-4" style={{ color: colors.dark }}>
        O Meu Carrinho
      </h1>

      <Row>
        <Col lg={8} className="mb-4">
          <Card className="shadow-sm" style={{ borderColor: colors.secondary }}>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Preço</th>
                    <th>Quantidade</th>
                    <th>Subtotal</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {cartItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{
                                width: "60px",
                                height: "60px",
                                objectFit: "cover",
                                marginRight: "15px",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "60px",
                                height: "60px",
                                backgroundColor: colors.background,
                                marginRight: "15px",
                              }}
                              className="d-flex align-items-center justify-content-center"
                            >
                              <i
                                className="bi bi-image"
                                style={{ color: colors.secondary }}
                              ></i>
                            </div>
                          )}
                          <div>
                            <h6 className="mb-0" style={{ color: colors.dark }}>
                              {item.name}
                              {item.variant && item.variant.name && ` - ${item.variant.name}`}
                            </h6>
                            {item.isDigital && (
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: colors.secondary,
                                  color: colors.light,
                                }}
                              >
                                Digital
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ color: colors.dark }}>
                        {formatPrice(item.price)}€
                      </td>
                      <td style={{ width: "150px" }}>
                        <Form.Control
                          type="number"
                          min="1"
                          max={item.stock ?? undefined}
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(item.id, e.target.value)
                          }
                          style={{
                            width: "80px",
                            borderColor: colors.secondary,
                          }}
                        />
                      </td>
                      <td style={{ color: colors.dark }}>
                        {formatPrice(item.price * item.quantity)}€
                      </td>
                      <td>
                        <Button
                          variant="link"
                          className="p-0"
                          onClick={() => removeFromCart(item.id)}
                          style={{ color: colors.danger }}
                          aria-label={`Remover ${item.name} do carrinho`}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="text-end">
                <Button
                  variant="link"
                  className="p-0"
                  onClick={clearCart}
                  style={{ color: colors.danger }}
                >
                  Esvaziar carrinho
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="shadow-sm" style={{ borderColor: colors.secondary }}>
            <Card.Header
              style={{ backgroundColor: colors.primary, color: colors.dark }}
            >
              <h5 className="mb-0">Resumo do Pedido</h5>
            </Card.Header>
            <Card.Body>
              {totalsError && (
                <Alert variant="danger" className="py-2">
                  {totalsError}
                </Alert>
              )}
              {totalsLoading && (
                <div className="text-center mb-3">
                  <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">A calcular totais...</span>
                  </Spinner>
                </div>
              )}
              <div className="d-flex justify-content-between mb-3">
                <span style={{ color: colors.textGray }}>Subtotal</span>
                <span style={{ color: colors.dark }}>
                  {renderAmount(orderTotals?.subtotalWithTax)}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span style={{ color: colors.textGray }}>
                  <span>{taxLabel(orderTotals)}</span>
                  <small className="d-block">incluído no subtotal</small>
                </span>
                <span style={{ color: colors.dark }}>
                  {renderAmount(orderTotals?.totalTax)}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span style={{ color: colors.textGray }}>Portes</span>
                <span style={{ color: colors.dark }}>
                  {orderTotals && orderTotals.shippingCost === 0
                    ? "Grátis"
                    : renderAmount(orderTotals?.shippingCost)}
                </span>
              </div>
              <hr />
              <div className="d-flex justify-content-between mb-3">
                <strong style={{ color: colors.dark }}>Total</strong>
                <strong style={{ color: colors.dark }}>
                  {renderAmount(orderTotals?.totalWithTax)}
                </strong>
              </div>

              <div className="d-grid gap-2">
                <Button
                  as={Link}
                  to="/checkout"
                  size="lg"
                  style={{
                    backgroundColor: colors.secondary,
                    borderColor: colors.secondary,
                    color: colors.light,
                  }}
                >
                  Finalizar Compra
                </Button>
                <Button
                  variant="outline-primary"
                  as={Link}
                  to="/produtos"
                  style={{
                    borderColor: colors.secondary,
                    color: colors.secondary,
                    backgroundColor: colors.light,
                  }}
                >
                  Continuar a comprar
                </Button>
              </div>
            </Card.Body>
          </Card>

          {freeShippingMessage() && (
            <Alert
              className="mt-4"
              style={{
                backgroundColor: colors.secondary,
                color: colors.light,
                borderColor: colors.secondary,
              }}
            >
              <i className="bi bi-info-circle-fill me-2"></i>
              {freeShippingMessage()}
            </Alert>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default CartPage;
